/**
 * @fileOverview The `data-help` anchors of the personnel planning board, which its markup cannot
 * carry.
 *
 * Every other region the walkthrough points at is an attribute in one of our own templates. The
 * board's day cells and bars are not ours: the gantt library draws them out of a virtualised layer,
 * one element per day per row, thrown away and rebuilt as the board scrolls. There is no prop to
 * hand an attribute through, and there is nothing stable to name anyway — "the empty days of this
 * week" is a place in the plan, not a place in the markup.
 *
 * So the walkthrough picks the elements itself and paints its ids onto them. **Only inside the
 * frame** ({@link IS_VIEW_HELP_EMBED}): outside it nothing runs, the board keeps the DOM it always
 * had, and the reader's own calendar is never walked over by an interval.
 *
 * What it picks, and why those:
 *
 * - a **free stretch of days** in one person's row — the hole in the plan a planner would fill. The
 *   sample calendar has exactly one from today on, so this is Erika's fortnight between the
 *   foundations and the shell (`samples/personnelplanning.ts`); its first day, its third and its
 *   fifth are the ends of the drags;
 * - the **block the board draws over a marked day**, which is where the right button has to be
 *   pressed: the menu of a marked stretch hangs off that block, and the day under it answers a
 *   right-click with nothing;
 * - the **day above it** in the first project's row, which is where those days are dropped to become
 *   an assignment;
 * - and, once the drop has made one, the **new assignment** in the person's row — its id is minted
 *   in the browser, so it is found by where it sits rather than by what it is called;
 * - and in the **name columns**, the name cell of the first project and of the first person, which
 *   answer the right button with their own menus. The cell and not the row: the library listens for
 *   the right-click on each cell, and an event sent to the row would never reach it.
 *
 * The picks are made **once**, as soon as the board shows a long enough stretch, and then only
 * re-applied: the cell element for a date is replaced whenever the board re-renders, and an anchor
 * that moved to another day between two steps would point at days the reader never saw marked.
 *
 * See docs/view-help.md ("The right button, and dragging") and docs/view-help/personnelplanning.md.
 */

import { onBeforeUnmount, onMounted } from "vue"
import { IS_VIEW_HELP_EMBED } from "@/services/viewHelp/embed"
import { useCalendarStore } from "@/store/calendar"

/** How often the board is looked at — and re-marked, only if it has drawn or scrolled since. */
const MARK_MS = 150

/** The two lanes, by the anchors `GanttSection` already carries. */
const LANE = {
  projects: '[data-help="planning-section-projects"]',
  employees: '[data-help="planning-section-employees"]',
} as const

type Lane = keyof typeof LANE

/** Days between the first day of the demonstration and the day the bar is later dragged onto. */
const MOVE_BY_DAYS = 4

/** Days the demonstration needs to be free: three to mark, and room for the bar it moves. */
const NEEDED_DAYS = MOVE_BY_DAYS + 3

/** How far inside its lane a day has to be to be picked. See {@link isShown}. */
const EDGE = 48

/** One id, and the cell it belongs on — the day it names, not the element, which the board recycles. */
interface CellMark {
  "data-help": string
  lane: Lane
  row: string
  date: string
}

/** Decided once (see the file comment), then re-applied on every tick. */
let marks: CellMark[] | null = null
let timer: ReturnType<typeof setInterval> | null = null

/**
 * Whether the board has changed since it was last marked: redrawn (a `MutationObserver`) or scrolled.
 * The tick does nothing otherwise. Every run of {@link apply} scans both lanes and measures cells and
 * bars, and the frame shares the host's main thread — run every 150ms regardless, it forced a layout
 * under the camera's and the pointer's animations for the whole planning walkthrough, and retried a
 * failed {@link decide} forever. Now it runs when there is something new to mark.
 */
let dirty = true
let observer: MutationObserver | null = null
const markDirty = () => {
  dirty = true
}

function tick() {
  if (!dirty) return
  dirty = false
  apply()
}

/** A day as the cells carry it, in the reader's own timezone rather than UTC. */
function iso(at: Date): string {
  const month = `${at.getMonth() + 1}`.padStart(2, "0")
  const day = `${at.getDate()}`.padStart(2, "0")
  return `${at.getFullYear()}-${month}-${day}`
}

const today = () => iso(new Date())

/** The day after a `yyyy-mm-dd`. */
function nextDay(date: string): string {
  const at = new Date(`${date}T00:00:00`)
  at.setDate(at.getDate() + 1)
  return iso(at)
}

const laneElement = (lane: Lane) => document.querySelector<HTMLElement>(LANE[lane])

const cellOn = (lane: HTMLElement, row: string, date: string) =>
  lane.querySelector<HTMLElement>(`.diagram-cell[data-row="${row}"][data-date="${date}"]`)

/**
 * One row of a lane with what is drawn in it. The row element carries no row number — the cells do,
 * and the library reads it off them the same way (`diagram-row` holds the day cells and the bars).
 */
interface Row {
  key: string
  cells: HTMLElement[]
  bars: HTMLElement[]
}

function rowsOf(lane: HTMLElement): Row[] {
  return Array.from(lane.querySelectorAll<HTMLElement>(".diagram-row"))
    .map(row => {
      const cells = Array.from(row.querySelectorAll<HTMLElement>(".diagram-cell[data-date][data-row]"))
      cells.sort((a, b) => a.dataset.date!.localeCompare(b.dataset.date!))
      return {
        key: cells[0]?.dataset.row ?? "",
        cells,
        bars: Array.from(row.querySelectorAll<HTMLElement>(".timeline-elem")),
      }
    })
    .filter(row => row.key !== "")
}

const rowOf = (lane: HTMLElement, key: string) => rowsOf(lane).find(row => row.key === key) ?? null

/**
 * Whether a row of the employee lane is one person — the only kind the demonstration's days may be
 * marked on. Read from the calendar, not the DOM, which cannot tell them apart:
 *
 * - The lane ends in an **empty row** the gantt library draws below the last resource. Free on every day,
 *   it always had the longest free stretch, so the days were marked on a row that belongs to nobody —
 *   and the drop onto the project assigned nobody (`parseEmployeeSelection` found no resource at that
 *   index): no bar, and `move`, `menu-span` and `editor`, which work on that bar, were skipped.
 * - A **team** row (`MULTIPLE`) assigns all its members, and the bar then appears in their rows rather
 *   than in the team's, where {@link newAssignment} looks for it.
 *
 * Top-level rows only: the key is the resource's position in the lane ("2"); a member row inside an
 * expanded team carries "1-2".
 */
function isPersonRow(key: string): boolean {
  if (key.includes("-")) return false
  const resource = useCalendarStore().getVisibleEmployees[Number(key) - 1]
  return resource?.type === "SINGLE"
}

/**
 * Whether a cell is on screen — inside the box that clips it as well as inside the window, with room
 * to spare at the sides.
 *
 * Measured against `.diagram`, the box the days scroll in, and not against the whole lane: the lane
 * includes the name columns, and a day just inside those is at the very edge of what scrolls. The
 * margin is what keeps the walkthrough from picking one: bringing the pointer to a target brings it
 * *comfortably* into view (`scrollTargetFor`), so a day picked at the edge would have the board slide
 * under it before the drag even started.
 */
function isShown(el: HTMLElement, lane: HTMLElement): boolean {
  const box = el.getBoundingClientRect()
  const within = (el.closest<HTMLElement>(".diagram") ?? lane).getBoundingClientRect()
  if (box.width < 4 || box.height < 4) return false
  if (box.left < Math.max(0, within.left) + EDGE || box.right > Math.min(window.innerWidth, within.right) - EDGE) {
    return false
  }
  return box.top >= Math.max(0, within.top) && box.bottom <= Math.min(window.innerHeight, within.bottom)
}

/** Whether any bar of the row covers this day. A cell under a bar is one the pointer cannot mark. */
const isFree = (cell: HTMLElement, bars: HTMLElement[]) => {
  const box = cell.getBoundingClientRect()
  return !bars.some(bar => {
    const over = bar.getBoundingClientRect()
    return over.left < box.right - 2 && over.right > box.left + 2
  })
}

/** Consecutive free days from today on, in one row — the longest such run the board is showing. */
function freeRun(cells: HTMLElement[], bars: HTMLElement[], lane: HTMLElement): HTMLElement[] {
  const from = today()
  let best: HTMLElement[] = []
  let run: HTMLElement[] = []
  for (const cell of cells) {
    const date = cell.dataset.date!
    const follows = run.length === 0 || nextDay(run[run.length - 1].dataset.date!) === date
    const usable = date >= from && isShown(cell, lane) && isFree(cell, bars)
    run = usable && follows ? [...run, cell] : usable ? [cell] : []
    if (run.length > best.length) best = run
  }
  return best
}

/** Monday, where there is one with room after it — a demonstration reads better on a working week. */
function firstDay(run: HTMLElement[]): number {
  const monday = run.findIndex(
    (cell, index) => new Date(`${cell.dataset.date!}T00:00:00`).getDay() === 1 && index + NEEDED_DAYS <= run.length
  )
  return monday === -1 ? 0 : monday
}

/**
 * The days the demonstration works on, or null while the board is not showing enough of them —
 * as it opens it is months before anybody is planned, where every day is free and none of them is
 * the day the walkthrough means.
 */
function decide(): CellMark[] | null {
  const employees = laneElement("employees")
  const projects = laneElement("projects")
  if (!employees || !projects) return null

  let picked: { row: string; run: HTMLElement[] } | null = null
  for (const row of rowsOf(employees).filter(candidate => isPersonRow(candidate.key))) {
    const run = freeRun(row.cells, row.bars, employees)
    if (run.length > (picked?.run.length ?? 0)) picked = { row: row.key, run }
  }
  if (!picked) return null

  const start = firstDay(picked.run)
  if (start + NEEDED_DAYS > picked.run.length) return null
  const day = (offset: number) => picked!.run[start + offset].dataset.date!

  // The projects lane's first row is the calendar's first project, which is the row the marked days
  // are dropped on. Its cell for the same day keeps the drag short and straight down.
  const above = cellOn(projects, "1", day(0))
  if (!above || !isShown(above, projects)) return null

  return [
    { "data-help": "planning-day-from", lane: "employees", row: picked.row, date: day(0) },
    { "data-help": "planning-day-to", lane: "employees", row: picked.row, date: day(2) },
    { "data-help": "planning-day-later", lane: "employees", row: picked.row, date: day(MOVE_BY_DAYS) },
    { "data-help": "planning-project-day", lane: "projects", row: "1", date: day(0) },
  ]
}

/** Puts an id on one element, and takes it off whatever was carrying it before. */
function only(id: string, wanted: HTMLElement | null) {
  for (const el of document.querySelectorAll<HTMLElement>(`[data-help="${id}"]`)) {
    if (el !== wanted) el.removeAttribute("data-help")
  }
  wanted?.setAttribute("data-help", id)
}

/**
 * The name cell of a top-level row in a lane's name column — the first one, or the one for `id`. The
 * table is the library's too (`GanttTableRow`): one `tr` per row carrying the resource's id.
 */
function nameCell(lane: Lane, id?: string): HTMLElement | null {
  const el = laneElement(lane)
  if (!el) return null
  const rows = Array.from(el.querySelectorAll<HTMLElement>('tr.table-data-row[data-depth="0"]')).filter(
    row => row.dataset.id && (id === undefined || row.dataset.id === id)
  )
  return rows[0]?.querySelector<HTMLElement>("td.table-cell") ?? null
}

/**
 * Whether the board already starts on today — what its Heute button would do. Every lane showing days
 * has today's column at the left edge of the box its days scroll in (`.diagram`), which is exactly
 * where Heute puts it. Pure DOM, so the player can ask it of the frame's document (`showToday`).
 */
export function isBoardOnToday(doc: Document): boolean {
  const now = new Date()
  const date = `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}-${`${now.getDate()}`.padStart(2, "0")}`
  const diagrams = Array.from(doc.querySelectorAll<HTMLElement>('[data-help^="planning-section-"] .diagram')).filter(
    diagram => diagram.getBoundingClientRect().width > 0
  )
  if (!diagrams.length) return false
  return diagrams.every(diagram => {
    const cell = diagram.querySelector<HTMLElement>(`.diagram-cell[data-date="${date}"]`)
    if (!cell) return false
    const box = cell.getBoundingClientRect()
    return Math.abs(box.left - diagram.getBoundingClientRect().left) < box.width / 2
  })
}

/** Re-applies every id to the element drawing it now. */
function apply() {
  // The name columns need nothing decided: they are there as soon as the board is. A person, not a
  // team, whose menu is another one (`editTeamTableContextMenu`).
  const person = useCalendarStore().getData.employees.resources.find(
    (resource: { type?: string }) => resource.type === "SINGLE"
  ) as { id?: string | number } | undefined
  only("planning-name-project", nameCell("projects"))
  only("planning-name-person", person?.id === undefined ? null : nameCell("employees", String(person.id)))

  if (!marks) marks = decide()
  if (!marks) return

  for (const mark of marks) {
    const lane = laneElement(mark.lane)
    only(mark["data-help"], lane ? cellOn(lane, mark.row, mark.date) : null)
  }

  // And the two that are only there once the pointer has made them. The block over a marked day is
  // what the right button has to be pressed on: the menu of a marked stretch hangs off that element
  // and not off the day under it, which answers a right-click with nothing at all.
  const lane = laneElement("employees")
  const first = lane ? cellOn(lane, marks[0].row, marks[0].date) : null
  for (const made of [
    { "data-help": "planning-day-marked", el: first?.querySelector<HTMLElement>(".cell-select") ?? null },
    { "data-help": "planning-span-new", el: newAssignment() },
  ]) {
    only(made["data-help"], made.el)
  }
}

/**
 * The bar the demonstration's own drop made, if it has happened: the one in the marked person's row
 * that starts somewhere in the free stretch. Its id is minted in the browser
 * (`useEmployeeAssignment`), so there is nothing to look it up by but where it sits — and "somewhere
 * in the stretch" rather than "on the first marked day", because the step after the drop drags this
 * same bar a few days along and it has to stay the same anchor.
 */
function newAssignment(): HTMLElement | null {
  const lane = laneElement("employees")
  if (!lane || !marks) return null
  const row = rowOf(lane, marks[0].row)
  const from = cellOn(lane, marks[0].row, marks[0].date)
  const last = cellOn(lane, marks[2].row, marks[2].date)
  if (!row || !from || !last) return null
  const start = from.getBoundingClientRect()
  const end = last.getBoundingClientRect()
  return (
    row.bars.find(bar => {
      const left = bar.getBoundingClientRect().left
      return left > start.left - start.width / 2 && left < end.right + start.width / 2
    }) ?? null
  )
}

/**
 * Marks the board for the walkthrough, in the frame and nowhere else. Called by
 * `PersonnelPlanningDetail`; one line there, and nothing at all in the real application.
 */
export function useBoardHelpAnchors() {
  if (!IS_VIEW_HELP_EMBED) return
  onMounted(() => {
    marks = null
    dirty = true
    // Class and style too: the board draws a day selection by changing them on cells it already has.
    // Not `data-help`, which `only` sets itself — marking would re-mark forever.
    observer = new MutationObserver(markDirty)
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    })
    document.addEventListener("scroll", markDirty, true)
    timer = setInterval(tick, MARK_MS)
  })
  onBeforeUnmount(() => {
    if (timer) clearInterval(timer)
    timer = null
    observer?.disconnect()
    observer = null
    document.removeEventListener("scroll", markDirty, true)
    marks = null
  })
}
